import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  UserCircle2,
  Search,
  Plus,
  Pencil,
  Eye,
  Check,
  X,
  RotateCcw,
  Loader2,
  FlaskConical,
  List as ListIcon,
  ChevronLeft,
  ChevronRight,
  Building2,
  TestTube2,
  ArrowRightLeft,
  Briefcase,
} from "lucide-react";
import {
  fetchList,
  approveCentre,
  rejectCentre,
  resubmitCentre,
  resetWizard,
  beginEdit,
  selectOnboardingWorkflow,
} from "../../store/ccOnboardingSlice";
import ccOnboardingApi, {
  STATUS_CODES,
  STATUS_LABELS,
  currentUserCode,
  currentUserName,
} from "../../services/ccOnboardingApi";
import corporateApi from "../../services/corporateApi";
import { toast } from "react-toastify";
import CCOnboarding, { SearchableSelect, DateInput } from "./ccOnboarding";
import LabOnboarding from "./LabOnboarding";
import RoutedLabOrdersList from "./RoutedLabOrdersList";
import CorporateList from "./CorporateList";

const MODE_CONFIG = {
  creator: { persona: "CREATOR", label: "Onboarding", approverLevel: null },
  businessApproval: {
    persona: "APPROVER",
    label: "Business Approval",
    approverLevel: 1,
    remarks: "Documents verified",
    canResubmit: true,
  },
  financeApproval: {
    persona: "APPROVER",
    label: "Finance Approval",
    approverLevel: 2,
    remarks: "Bank details verified",
    canResubmit: true,
  },
};

function getLoggedInUser() {
  return { code: currentUserCode(), name: currentUserName() };
}

function useWorkflowUser(mode) {
  const [searchParams] = useSearchParams();
  const levelParam = searchParams.get("approverLevel") || searchParams.get("level");
  const modeParam = searchParams.get("mode");

  return useMemo(() => {
    let effectiveMode = modeParam || mode;
    if (levelParam === "1" || levelParam === 1) effectiveMode = "businessApproval";
    if (levelParam === "2" || levelParam === 2) effectiveMode = "financeApproval";

    const config = MODE_CONFIG[effectiveMode] || MODE_CONFIG.creator;
    const loggedInUser = getLoggedInUser();
    return {
      ...config,
      approverLevel:
        levelParam !== null && levelParam !== undefined
          ? Number(levelParam)
          : config.approverLevel,
      code: loggedInUser.code,
      name: loggedInUser.name,
    };
  }, [mode, modeParam, levelParam]);
}

const CREATOR_FILTERS = [
  { statusCode: "", label: "All" },
  { statusCode: STATUS_CODES.PENDING_APPROVAL, label: "Waiting for Approval" },
  { statusCode: STATUS_CODES.APPROVED, label: "Onboarded" },
  { statusCode: STATUS_CODES.DRAFT, label: "Draft" },
  { statusCode: STATUS_CODES.REJECTED, label: "Rejected" },
];

const APPROVER_FILTERS = [
  { statusCode: `${STATUS_CODES.PENDING_APPROVAL},${STATUS_CODES.PARTIALLY_APPROVED}`, label: "Waiting for Approval" },
  { statusCode: STATUS_CODES.APPROVED, label: "Onboarded" },
  { statusCode: STATUS_CODES.REJECTED, label: "Rejected" },
];

function extractRows(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  return payload.content || payload.items || payload.data || payload.records || [];
}

function extractPagination(payload) {
  if (!payload) return { totalPages: 0, totalElements: 0, page: 0, size: 10 };
  return {
    totalPages: payload.totalPages || payload.total_pages || 0,
    totalElements: payload.totalElements || payload.total_elements || 0,
    page: payload.page || payload.number || 0,
    size: payload.size || payload.pageSize || 10,
  };
}

function rowStatusCode(row) {
  const code = row.statusCode ?? row.status;
  return code === undefined || code === null ? null : Number(code);
}

function rowCode(row) {
  return row.centerCode || row.code || row.ccCode || "";
}

function rowDraftKey(row) {
  return row.draftKey || "";
}

function rowName(row) {
  return row.labName || row.ccName || row.name || "Untitled";
}

function rowEclinicName(row) {
  return row.eclinicName || row.eclinicCode || "—";
}

function rowSmName(row) {
  const c = (row.contacts && row.contacts[0]) || {};
  const name = [c.firstName, c.lastName].filter(Boolean).join(" ");
  return row.contactName || row.contactPerson || row.smName || name || "—";
}

function rowSmNumber(row) {
  const c = (row.contacts && row.contacts[0]) || {};
  return row.contactNumber || row.contactMobileNumber || row.mobileNumber || row.smNumber || row.smMobileNumber || c.mobileNumber || "—";
}

function rowLabType(row) {
  const t = row.diagnosticType || row.labType || row.type || row.diagnosticCategory || row.diagnosticCategoryName;
  if (!t) return "—";
  if (t === 1 || t === "1" || String(t).toLowerCase() === "pathology") return "Pathology";
  if (t === 2 || t === "2" || String(t).toLowerCase() === "radiology") return "Radiology";
  if (t === 3 || t === "3" || String(t).toLowerCase() === "both") return "Both";
  return String(t);
}

function rowCity(row) {
  return row.city || row.district || row.area || row.location || "—";
}

function rowRejectionReason(row) {
  return (
    row.rejectionRemarks ||
    row.resubmitRemarks ||
    row.lastWorkflowRemarks ||
    row.rejectionReason ||
    row.rejectReason ||
    row.remarks ||
    ""
  );
}

function rowUserRole(row) {
  return row.userRole || row.userRoleCode || "—";
}

function rowCreatedDateRaw(row) {
  return (
    row.createdDate ||
    row.createdAt ||
    row.createDate ||
    row.creationDate ||
    row.created_date ||
    row.created_at ||
    row.agreementDate ||
    ""
  );
}

function rowCreatedDate(row) {
  const d = rowCreatedDateRaw(row);
  if (!d) return "—";
  try {
    if (typeof d === "string" && d.length >= 10) {
      const datePart = d.split("T")[0].split(" ")[0];
      const parts = datePart.split("-");
      if (parts.length === 3 && parts[0].length === 4) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
    }
    const dt = new Date(d);
    if (!isNaN(dt.getTime())) {
      const day = String(dt.getDate()).padStart(2, "0");
      const month = String(dt.getMonth() + 1).padStart(2, "0");
      const year = dt.getFullYear();
      return `${day}/${month}/${year}`;
    }
    return String(d);
  } catch {
    return String(d);
  }
}

function rowRemarks(row) {
  return (
    row.rejectionRemarks ||
    row.resubmitRemarks ||
    row.lastWorkflowRemarks ||
    row.rejectionReason ||
    row.rejectReason ||
    row.remarks ||
    "—"
  );
}

function StatusPill({ statusCode, hasPendingEdit = false, rejectionReason = "" }) {
  const styleMap = {
    [STATUS_CODES.DRAFT]: "bg-slate-100 text-slate-600",
    [STATUS_CODES.PENDING_APPROVAL]: "bg-amber-100 text-amber-700",
    [STATUS_CODES.PARTIALLY_APPROVED]: "bg-amber-100 text-amber-700",
    [STATUS_CODES.APPROVED]: "bg-emerald-100 text-emerald-700",
    [STATUS_CODES.REJECTED]: "bg-rose-100 text-rose-700",
  };

  const isRejected = statusCode === STATUS_CODES.REJECTED;

  return (
    <div className="flex items-center">
      <span
        className={`inline-flex flex-wrap items-center gap-1.5 ${isRejected ? "cursor-help" : ""}`}
        title={isRejected ? rejectionReason || "Rejected" : ""}
      >
        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${styleMap[statusCode] || "bg-slate-100 text-slate-600"}`}>
          {STATUS_LABELS[statusCode] || "Unknown"}
        </span>
        {hasPendingEdit && statusCode === STATUS_CODES.APPROVED && (
          <span
            className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700"
            title="An edit is waiting for approval. Open the record to see what changed."
          >
            Edit pending
          </span>
        )}
      </span>
    </div>
  );
}

function RemarksModal({ open, title = "Remarks", ccName = "", ccCode = "", defaultValue = "", onCancel, onConfirm }) {
  const [remarks, setRemarks] = useState("");
  useEffect(() => {
    if (open) setRemarks(defaultValue);
  }, [open, defaultValue]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl">
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        {(ccName || ccCode) && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-slate-600">
            {ccName && <span className="font-semibold text-slate-800">{ccName}</span>}
            {ccCode && <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-600">({ccCode})</span>}
          </div>
        )}
        <textarea
          rows={3}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Enter reason / remarks"
          className="mt-3 w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-50"
        />
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(remarks)}
            disabled={!remarks.trim()}
            className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

function Pagination({ currentPage, totalPages, onPageChange, pageSize, onPageSizeChange, totalElements }) {
  const pageSizeOptions = [10, 20, 50, 100];

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisible = 5;
    let start = Math.max(0, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages - 1, start + maxVisible - 1);

    if (end - start < maxVisible - 1) {
      start = Math.max(0, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  if (totalPages <= 0) return null;

  const startRecord = currentPage * pageSize + 1;
  const endRecord = Math.min((currentPage + 1) * pageSize, totalElements);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-slate-200">
      <div className="flex items-center gap-4 text-sm text-slate-500">
        <div className="flex items-center gap-2">
          <span>Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="rounded-lg border border-slate-200 px-2 py-1 text-sm outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-50"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
        <span>{totalElements > 0 ? `${startRecord}-${endRecord} of ${totalElements}` : "0 records"}</span>
      </div>

      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 0}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        {getPageNumbers().map((pageNum) => (
          <button
            key={pageNum}
            onClick={() => onPageChange(pageNum)}
            className={`rounded-lg px-3.5 py-1.5 text-sm transition-colors ${
              currentPage === pageNum
                ? "bg-indigo-600 text-white hover:bg-indigo-700"
                : "border border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
          >
            {pageNum + 1}
          </button>
        ))}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages - 1}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function OnboardingTable({ rows, loading, actions, pagination, onPageChange, onPageSizeChange, onboardingType = "CC" }) {
  const isLab = onboardingType === "LAB";

  return (
    <div className="space-y-4">
      <div className="overflow-auto max-h-[calc(100vh-320px)] min-h-[350px] rounded-xl border border-slate-200 shadow-xs">
        <table className="w-full min-w-[980px] text-left text-sm border-separate border-spacing-0">
          <thead className="sticky top-0 z-10 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500 shadow-xs">
            <tr>
              <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 border-b border-slate-200">Code</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 border-b border-slate-200">{isLab ? "Lab Name" : "CC Name"}</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 border-b border-slate-200">Lab Type</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 border-b border-slate-200">{isLab ? "City" : "Eclinic Name"}</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 border-b border-slate-200">{isLab ? "Contact Person" : "SM Name"}</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 border-b border-slate-200">{isLab ? "Contact Phone" : "SM Number"}</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 border-b border-slate-200">Created Date</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 border-b border-slate-200">Status</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 border-b border-slate-200">Remarks</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-right border-b border-slate-200">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-slate-400">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                </td>
              </tr>
            )}
            {!loading && !rows.length && (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-slate-400">
                  No records found
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((row) => {
                const remarksText = rowRemarks(row);
                const hasRemarks = remarksText && remarksText !== "—";
                return (
                  <tr key={rowCode(row) || rowDraftKey(row) || rowName(row)} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-700">{rowCode(row) || rowDraftKey(row) || "—"}</td>
                    <td className="px-4 py-3 text-slate-700 font-medium">{rowName(row)}</td>
                    <td className="px-4 py-3 text-slate-600">{rowLabType(row)}</td>
                    <td className="px-4 py-3 text-slate-500">{isLab ? rowCity(row) : rowEclinicName(row)}</td>
                    <td className="px-4 py-3 text-slate-500">{rowSmName(row)}</td>
                    <td className="px-4 py-3 text-slate-500">{rowSmNumber(row)}</td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{rowCreatedDate(row)}</td>
                    <td className="px-4 py-3">
                      <StatusPill
                        statusCode={rowStatusCode(row)}
                        hasPendingEdit={!!row.hasPendingEdit}
                        rejectionReason={rowRejectionReason(row)}
                      />
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {hasRemarks ? (
                        <span
                          className={`max-w-[200px] truncate block ${
                            rowStatusCode(row) === STATUS_CODES.REJECTED ? "text-rose-600 font-medium" : "text-slate-600"
                          }`}
                          title={remarksText}
                        >
                          {remarksText}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">{actions(row)}</div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {!loading && pagination && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          onPageChange={onPageChange}
          pageSize={pagination.size}
          onPageSizeChange={onPageSizeChange}
          totalElements={pagination.totalElements}
        />
      )}
    </div>
  );
}

function ActionButton({ icon: Icon, label, onClick, tone = "slate", disabled = false }) {
  const toneMap = {
    slate: "border-slate-200 text-slate-600 hover:bg-slate-50",
    emerald: "border-emerald-200 text-emerald-600 hover:bg-emerald-50",
    rose: "border-rose-200 text-rose-600 hover:bg-rose-50",
    indigo: "border-indigo-200 text-indigo-600 hover:bg-indigo-50",
    amber: "border-amber-200 text-amber-700 hover:bg-amber-50",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${toneMap[tone]}`}
    >
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
}

function FilterBar({ filters, onChange, statusOptions, onboardingType = "CC" }) {
  const isLab = onboardingType === "LAB";
  const [eclinicOptions, setEclinicOptions] = useState([]);

  useEffect(() => {
    if (isLab) return;
    let active = true;
    ccOnboardingApi
      .getEclinicDropdown()
      .then((res) => {
        if (active) setEclinicOptions(res?.data?.data || res?.data || []);
      })
      .catch(() => {
        if (active) setEclinicOptions([]);
      });
    return () => {
      active = false;
    };
  }, [isLab]);

  const set = (field) => (e) => onChange({ ...filters, [field]: e.target.value });

  return (
    <div className={`grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 ${isLab ? "lg:grid-cols-6" : "lg:grid-cols-7"} text-left items-end`}>
      {statusOptions && (
        <div className="text-left">
          <label className="mb-0.5 block text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Status</label>
          <SearchableSelect
            value={filters.statusCode}
            options={statusOptions}
            placeholder="All Statuses"
            searchPlaceholder="Search status…"
            getLabel={(f) => (typeof f === "string" ? f : f.label || "")}
            getKey={(f) => (typeof f === "string" ? f : String(f.statusCode ?? ""))}
            onChange={(val) => onChange({ ...filters, statusCode: val })}
            compact={true}
          />
        </div>
      )}
      <div className="text-left">
        <label className="mb-0.5 block text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          {isLab ? "Lab Name" : "CC Name"}
        </label>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <input
            value={filters.ccName || filters.labName || ""}
            onChange={(e) => onChange({ ...filters, ccName: e.target.value, labName: e.target.value })}
            placeholder={isLab ? "Search Lab name" : "Search CC name"}
            className="w-full rounded-lg border border-slate-200 h-[34px] py-1 pl-8 pr-2.5 text-xs outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50 text-left"
          />
        </div>
      </div>
      <div className="text-left">
        <label className="mb-0.5 block text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          {isLab ? "Contact Person Name" : "SM Name"}
        </label>
        <input
          value={filters.smName || filters.contactName || ""}
          onChange={(e) => onChange({ ...filters, smName: e.target.value, contactName: e.target.value })}
          placeholder={isLab ? "Search Contact Person" : "Search SM name"}
          className="w-full rounded-lg border border-slate-200 px-2.5 h-[34px] py-1 text-xs outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50 text-left"
        />
      </div>
      <div className="text-left">
        <label className="mb-0.5 block text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          {isLab ? "Contact Person Mobile" : "SM Phone Number"}
        </label>
        <input
          value={filters.smNumber || filters.contactNumber || ""}
          onChange={(e) => onChange({ ...filters, smNumber: e.target.value, contactNumber: e.target.value })}
          placeholder={isLab ? "Search Contact mobile" : "Search SM number"}
          className="w-full rounded-lg border border-slate-200 px-2.5 h-[34px] py-1 text-xs outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50 text-left"
        />
      </div>
      <div className="text-left">
        <label className="mb-0.5 block text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          Lab Type
        </label>
        <SearchableSelect
          value={filters.diagnosticCategory || ""}
          options={[
            { label: "All Types", value: "" },
            { label: "Pathology", value: "pathology" },
            { label: "Radiology", value: "radiology" },
          ]}
          placeholder="All Types"
          searchPlaceholder="Search type…"
          getLabel={(o) => (typeof o === "string" ? o : o.label || o.value)}
          getKey={(o) => (typeof o === "string" ? o : o.value)}
          onChange={(val) => onChange({ ...filters, diagnosticCategory: val, diagnosticType: val })}
          compact={true}
        />
      </div>
      <div className="text-left">
        <label className="mb-0.5 block text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          Created Date
        </label>
        <DateInput
          value={filters.createdDate || ""}
          placeholder="YYYY-MM-DD"
          onChange={(val) => onChange({ ...filters, createdDate: val || "" })}
          compact={true}
        />
      </div>
      {!isLab && (
        <div className="text-left">
          <label className="mb-0.5 block text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Eclinic</label>
          <SearchableSelect
            value={filters.eclinicCode}
            options={eclinicOptions}
            placeholder="All Eclinics"
            searchPlaceholder="Search eclinic…"
            getLabel={(o) => (typeof o === "string" ? o : o.hospitalName || o.eclinicName || o.name || "")}
            getKey={(o) => (typeof o === "string" ? o : String(o.hospitalCode ?? o.eclinicCode ?? o.code ?? o.id ?? ""))}
            getSubtitle={(o) => (typeof o === "object" ? [o.city, o.state].filter(Boolean).join(", ") : "")}
            onChange={(val) => onChange({ ...filters, eclinicCode: val })}
            compact={true}
          />
        </div>
      )}
    </div>
  );
}

function applyListFilters(source, filters) {
  let rows = source;
  const nameSearch = (filters.ccName || filters.labName || "").trim().toLowerCase();
  if (nameSearch) {
    rows = rows.filter((r) => rowName(r).toLowerCase().includes(nameSearch));
  }
  const contactSearch = (filters.smName || filters.contactName || "").trim().toLowerCase();
  if (contactSearch) {
    rows = rows.filter((r) => rowSmName(r).toLowerCase().includes(contactSearch));
  }
  const phoneSearch = (filters.smNumber || filters.contactNumber || "").trim().toLowerCase();
  if (phoneSearch) {
    rows = rows.filter((r) => rowSmNumber(r).toLowerCase().includes(phoneSearch));
  }
  if (filters.eclinicCode) {
    rows = rows.filter((r) => String(r.eclinicCode ?? "") === String(filters.eclinicCode));
  }
  if (filters.diagnosticCategory || filters.diagnosticType) {
    const filterCat = String(filters.diagnosticCategory || filters.diagnosticType).trim().toLowerCase();
    if (filterCat) {
      rows = rows.filter((r) => {
        const type = rowLabType(r).toLowerCase();
        if (filterCat === "pathology") return type === "pathology" || type === "both" || type === "1" || type === "3";
        if (filterCat === "radiology") return type === "radiology" || type === "both" || type === "2" || type === "3";
        if (filterCat === "both") return type === "both" || type === "3";
        return type.includes(filterCat);
      });
    }
  }
  if (filters.createdDate) {
    const filterDate = String(filters.createdDate).trim();
    if (filterDate) {
      rows = rows.filter((r) => {
        const d = rowCreatedDateRaw(r);
        if (!d) return false;
        const dStr = String(d).split("T")[0].split(" ")[0];
        return dStr === filterDate || dStr.startsWith(filterDate);
      });
    }
  }
  return rows;
}

function CreatorList({ onEdit, onView, onAddNew, onboardingType = "CC" }) {
  const isLab = onboardingType === "LAB";
  const dispatch = useDispatch();
  const { list } = useSelector(selectOnboardingWorkflow);
  const [filters, setFilters] = useState({
    statusCode: "",
    ccName: "",
    labName: "",
    smName: "",
    contactName: "",
    smNumber: "",
    contactNumber: "",
    diagnosticCategory: "",
    createdDate: "",
    eclinicCode: "",
  });
  const [pagination, setPagination] = useState({
    page: 0,
    size: 10,
    totalPages: 0,
    totalElements: 0,
  });

  useEffect(() => {
    dispatch(
      fetchList({
        onboardingType,
        statusCode: filters.statusCode || undefined,
        ccName: filters.ccName || filters.labName || undefined,
        smName: filters.smName || filters.contactName || undefined,
        smNumber: filters.smNumber || filters.contactNumber || undefined,
        diagnosticCategory: filters.diagnosticCategory || undefined,
        diagnosticType: filters.diagnosticCategory || undefined,
        createdDate: filters.createdDate || undefined,
        eclinic: isLab ? undefined : filters.eclinicCode || undefined,
        page: pagination.page,
        size: pagination.size,
        sortDir: "desc",
      })
    );
  }, [
    dispatch,
    onboardingType,
    isLab,
    filters.statusCode,
    filters.ccName,
    filters.labName,
    filters.smName,
    filters.contactName,
    filters.smNumber,
    filters.contactNumber,
    filters.diagnosticCategory,
    filters.createdDate,
    filters.eclinicCode,
    pagination.page,
    pagination.size,
  ]);

  useEffect(() => {
    if (list.data) {
      const paginationInfo = extractPagination(list.data);
      setPagination((prev) => ({
        ...prev,
        totalPages: paginationInfo.totalPages,
        totalElements: paginationInfo.totalElements,
        page: paginationInfo.page || prev.page,
        size: paginationInfo.size || prev.size,
      }));
    }
  }, [list.data]);

  const rows = useMemo(() => applyListFilters(extractRows(list.data), filters), [list.data, filters]);

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newSize) => {
    setPagination((prev) => ({ ...prev, size: newSize, page: 0 }));
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  return (
    <div className="space-y-2">
      <div className="sticky top-16 z-20 bg-white/95 backdrop-blur-xs py-1 space-y-1.5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-xs font-semibold text-slate-500"></h2>
          <button
            type="button"
            onClick={onAddNew}
            className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 h-[32px]"
          >
            <Plus className="h-3.5 w-3.5" /> {isLab ? "New Lab Onboarding" : "New Onboarding"}
          </button>
        </div>

        <FilterBar filters={filters} onChange={handleFilterChange} statusOptions={CREATOR_FILTERS} onboardingType={onboardingType} />
      </div>

      <OnboardingTable
        rows={rows}
        loading={list.status === "loading"}
        onboardingType={onboardingType}
        actions={(row) => {
          const statusCode = rowStatusCode(row);
          const canEdit = statusCode !== STATUS_CODES.REJECTED;

          if (canEdit) {
            return (
              <ActionButton
                icon={Pencil}
                label="View / Edit"
                tone="indigo"
                onClick={() => onEdit({ centerCode: rowCode(row), draftKey: rowDraftKey(row) })}
              />
            );
          }
          return (
            <ActionButton
              icon={Eye}
              label="View"
              tone="slate"
              onClick={() => onView({ centerCode: rowCode(row), draftKey: rowDraftKey(row) })}
            />
          );
        }}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
}

const PENDING_APPROVAL_STATUSES = new Set([STATUS_CODES.PENDING_APPROVAL, STATUS_CODES.PARTIALLY_APPROVED]);

const DEFAULT_APPROVER_FILTERS = {
  statusCode: "11,12",
  ccName: "",
  labName: "",
  smName: "",
  contactName: "",
  smNumber: "",
  contactNumber: "",
  diagnosticCategory: "",
  createdDate: "",
  eclinicCode: "",
  search: "",
};

const DEFAULT_APPROVER_PAGINATION = {
  page: 0,
  size: 25,
  totalPages: 0,
  totalElements: 0,
};

function ApproverList({ user, onView, onEdit, filters, onFiltersChange, pagination, onPaginationChange, onboardingType = "CC" }) {
  const isLab = onboardingType === "LAB";
  const dispatch = useDispatch();
  const { list, workflowAction } = useSelector(selectOnboardingWorkflow);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [resubmitTarget, setResubmitTarget] = useState(null);
  const [busyCode, setBusyCode] = useState(null);

  const loadList = useCallback(() => {
    dispatch(
      fetchList({
        onboardingType,
        approverLevel:
          user.approverLevel !== null && user.approverLevel !== undefined
            ? user.approverLevel
            : undefined,
        statusCode: filters.statusCode || "11,12",
        eclinic: isLab ? undefined : filters.eclinicCode || undefined,
        ccName: filters.ccName || filters.labName || undefined,
        smName: filters.smName || filters.contactName || undefined,
        smNumber: filters.smNumber || filters.contactNumber || undefined,
        diagnosticCategory: filters.diagnosticCategory || undefined,
        diagnosticType: filters.diagnosticCategory || undefined,
        createdDate: filters.createdDate || undefined,
        search: filters.search || undefined,
        page: pagination.page,
        size: pagination.size || 25,
      })
    );
  }, [dispatch, onboardingType, isLab, user.approverLevel, filters, pagination.page, pagination.size]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (list.data) {
      const paginationInfo = extractPagination(list.data);
      onPaginationChange((prev) => ({
        ...prev,
        totalPages: paginationInfo.totalPages,
        totalElements: paginationInfo.totalElements,
        page: paginationInfo.page || prev.page,
        size: paginationInfo.size || prev.size,
      }));
    }
  }, [list.data]);

  const rows = useMemo(() => applyListFilters(extractRows(list.data), filters), [list.data, filters]);

  const handleApprove = async (row) => {
    setBusyCode(rowCode(row));
    await dispatch(
      approveCentre({ centerCode: rowCode(row), remarks: user.remarks || "Approved", userCode: user.code })
    );
    setBusyCode(null);
    loadList();
  };

  const handleReject = async (remarks) => {
    if (!rejectTarget) return;
    setBusyCode(rowCode(rejectTarget));
    await dispatch(rejectCentre({ centerCode: rowCode(rejectTarget), remarks, userCode: user.code }));
    setBusyCode(null);
    setRejectTarget(null);
    loadList();
  };

  const handleResubmit = async (remarks) => {
    if (!resubmitTarget) return;
    setBusyCode(rowCode(resubmitTarget));
    await dispatch(
      resubmitCentre({
        centerCode: rowCode(resubmitTarget),
        draftKey: rowDraftKey(resubmitTarget),
        userCode: user.code,
        remarks,
      })
    );
    setBusyCode(null);
    setResubmitTarget(null);
    loadList();
  };

  const handlePageChange = (newPage) => {
    onPaginationChange((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newSize) => {
    onPaginationChange((prev) => ({ ...prev, size: newSize, page: 0 }));
  };

  const handleFilterChange = (newFilters) => {
    onFiltersChange(newFilters);
    onPaginationChange((prev) => ({ ...prev, page: 0 }));
  };

  return (
    <div className="space-y-2">
      <div className="sticky top-16 z-20 bg-white/95 backdrop-blur-xs py-1">
        <FilterBar filters={filters} onChange={handleFilterChange} statusOptions={APPROVER_FILTERS} onboardingType={onboardingType} />
      </div>

      {workflowAction.status === "failed" && (
        <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-600">{workflowAction.error}</p>
      )}

      <OnboardingTable
        rows={rows}
        loading={list.status === "loading"}
        onboardingType={onboardingType}
        actions={(row) => {
          const statusCode = rowStatusCode(row);
          const isPending = PENDING_APPROVAL_STATUSES.has(statusCode);
          return (
            <>
              <ActionButton icon={Eye} label="View" tone="slate" onClick={() => onView({ centerCode: rowCode(row), draftKey: rowDraftKey(row) })} />
              {isPending && (
                <ActionButton
                  icon={Check}
                  label="Approve"
                  tone="emerald"
                  disabled={busyCode === rowCode(row)}
                  onClick={() => handleApprove(row)}
                />
              )}
              {isPending && (
                <ActionButton
                  icon={X}
                  label="Reject"
                  tone="rose"
                  disabled={busyCode === rowCode(row)}
                  onClick={() => setRejectTarget(row)}
                />
              )}
              {user.canResubmit && isPending && (
                <ActionButton
                  icon={RotateCcw}
                  label="Resubmit"
                  tone="amber"
                  disabled={busyCode === rowCode(row)}
                  onClick={() => setResubmitTarget(row)}
                />
              )}
            </>
          );
        }}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      <RemarksModal
        open={!!rejectTarget}
        title={isLab ? "Reject Diagnostic Lab" : "Reject Collection Center"}
        ccName={rejectTarget ? rowName(rejectTarget) : ""}
        ccCode={rejectTarget ? rowCode(rejectTarget) : ""}
        onCancel={() => setRejectTarget(null)}
        onConfirm={handleReject}
      />
      <RemarksModal
        open={!!resubmitTarget}
        title={isLab ? "Resubmit Diagnostic Lab" : "Resubmit Collection Center"}
        ccName={resubmitTarget ? rowName(resubmitTarget) : ""}
        ccCode={resubmitTarget ? rowCode(resubmitTarget) : ""}
        onCancel={() => setResubmitTarget(null)}
        onConfirm={handleResubmit}
      />
    </div>
  );
}

function CorporatePendingList({ onView, onApprove, onReject, busyId, refreshKey = 0 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    code: "",
    name: "",
    email: "",
    mobile: "",
  });
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.code?.trim()) count++;
    if (filters.name?.trim()) count++;
    if (filters.email?.trim()) count++;
    if (filters.mobile?.trim()) count++;
    return count;
  }, [filters]);

  const handleResetFilters = () => {
    setFilters({
      code: "",
      name: "",
      email: "",
      mobile: "",
    });
    setPage(0);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchPendingCorporates = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        page,
        size: pageSize,
        isApproved: 0,
      };
      if (filters.code?.trim()) {
        payload.code = filters.code.trim();
      }
      if (filters.name?.trim()) {
        payload.name = filters.name.trim();
      }
      if (filters.email?.trim()) {
        payload.email = filters.email.trim();
      }
      if (filters.mobile?.trim()) {
        payload.mobile = filters.mobile.trim();
      }
      const res = await corporateApi.getCorporateList(payload);
      let list = [];
      let total = 0;
      if (Array.isArray(res?.content)) {
        list = res.content;
        total = res.page?.totalElements ?? res.totalElements ?? res.content.length;
      } else if (Array.isArray(res?.data?.content)) {
        list = res.data.content;
        total = res.data.page?.totalElements ?? res.data.totalElements ?? res.data.content.length;
      } else if (Array.isArray(res?.data)) {
        list = res.data;
        total = res.totalElements ?? res.data.length;
      } else if (Array.isArray(res)) {
        list = res;
        total = res.length;
      }

      const pendingOnly = list.filter((c) => {
        const a = c?.isApproved;
        return a === 0 || a === "0" || a === null || a === undefined;
      });

      setRows(pendingOnly);
      setTotalRecords(total || pendingOnly.length);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to load corporate pending list");
      setRows([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters, refreshKey]);

  useEffect(() => {
    fetchPendingCorporates();
  }, [fetchPendingCorporates]);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (filters.name?.trim()) {
        const q = filters.name.trim().toLowerCase();
        if (!r.name?.toLowerCase().includes(q)) return false;
      }
      if (filters.code?.trim()) {
        const q = filters.code.trim().toLowerCase();
        if (!r.code?.toLowerCase().includes(q)) return false;
      }
      if (filters.email?.trim()) {
        const q = filters.email.trim().toLowerCase();
        if (!r.email?.toLowerCase().includes(q)) return false;
      }
      if (filters.mobile?.trim()) {
        const q = filters.mobile.trim();
        if (!String(r.mobile || "").includes(q)) return false;
      }
      return true;
    });
  }, [rows, filters]);

  const totalPages = Math.ceil(totalRecords / pageSize) || 1;

  return (
    <div className="space-y-4">
      <div className="sticky top-16 z-20 bg-white/95 backdrop-blur-xs py-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs font-medium text-slate-500">
            Showing <span className="font-semibold text-slate-700">{totalRecords}</span> pending corporate onboarding{totalRecords === 1 ? "" : "s"}
          </div>

          <div className="flex items-center gap-2">
            <div className="relative min-w-[200px] sm:min-w-[240px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={filters.name}
                onChange={(e) => {
                  setFilters((prev) => ({ ...prev, name: e.target.value }));
                  setPage(0);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setPage(0);
                    fetchPendingCorporates();
                  }
                }}
                placeholder="Search corporate name..."
                className="w-full rounded-xl border border-slate-300 bg-white py-1.5 pl-8 pr-7 text-xs text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100"
              />
              {filters.name && (
                <button
                  type="button"
                  onClick={() => {
                    setFilters((prev) => ({ ...prev, name: "" }));
                    setPage(0);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>

            <div className="relative" ref={filterRef}>
              <button
                type="button"
                onClick={() => setFilterOpen((prev) => !prev)}
                className={`flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-semibold transition ${
                  activeFilterCount > 0
                    ? "border-indigo-300 bg-indigo-50 text-indigo-700 shadow-2xs"
                    : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                }`}
              >
                <Search className="h-3.5 w-3.5 text-slate-500" />
                <span>Filters</span>
                {activeFilterCount > 0 && (
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-indigo-600 text-[10px] font-bold text-white">
                    {activeFilterCount}
                  </span>
                )}
              </button>

              {filterOpen && (
                <div className="absolute right-0 top-full z-50 mt-2 w-[420px] rounded-2xl border border-slate-200 bg-white shadow-2xl ring-1 ring-slate-900/10 overflow-hidden animate-in fade-in zoom-in-95">
                  <div className="flex items-center justify-between border-b border-slate-200/80 bg-slate-50/80 px-5 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-600 text-white shadow-2xs">
                        <Building2 className="h-4 w-4" />
                      </div>
                      <div className="text-left">
                        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800">
                          Filter Corporates
                        </h3>
                        <p className="text-[11px] text-slate-500">Refine the corporate records</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {activeFilterCount > 0 && (
                        <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-bold text-indigo-700">
                          {activeFilterCount} active
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => setFilterOpen(false)}
                        className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-200/70 hover:text-slate-700 transition"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      setFilterOpen(false);
                      setPage(0);
                      fetchPendingCorporates();
                    }}
                  >
                    <div className="p-5 space-y-3.5">
                      <div className="grid grid-cols-2 gap-3.5">
                        <div className="text-left">
                          <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                            Corporate Code
                          </label>
                          <input
                            type="text"
                            value={filters.code}
                            onChange={(e) => setFilters((prev) => ({ ...prev, code: e.target.value }))}
                            placeholder="e.g. CORP01"
                            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100"
                          />
                        </div>

                        <div className="text-left">
                          <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                            Email Address
                          </label>
                          <input
                            type="text"
                            value={filters.email}
                            onChange={(e) => setFilters((prev) => ({ ...prev, email: e.target.value }))}
                            placeholder="name@domain.com"
                            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100"
                          />
                        </div>

                        <div className="col-span-2 text-left">
                          <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                            Corporate Name
                          </label>
                          <div className="relative">
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                            <input
                              type="text"
                              value={filters.name}
                              onChange={(e) => setFilters((prev) => ({ ...prev, name: e.target.value }))}
                              placeholder="Search by corporate name"
                              className="w-full rounded-xl border border-slate-300 bg-white py-2 pl-8 pr-3 text-xs text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100"
                            />
                          </div>
                        </div>

                        <div className="col-span-2 text-left">
                          <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                            Mobile Number
                          </label>
                          <input
                            type="text"
                            maxLength={10}
                            value={filters.mobile}
                            onChange={(e) => setFilters((prev) => ({ ...prev, mobile: e.target.value.replace(/[^0-9]/g, "") }))}
                            placeholder="10-digit mobile"
                            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between border-t border-slate-200/80 bg-slate-50/80 px-5 py-3">
                      <button
                        type="button"
                        onClick={handleResetFilters}
                        className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 transition"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        <span>Reset all</span>
                      </button>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setFilterOpen(false)}
                          className="rounded-xl border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition shadow-2xs"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="flex items-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-1.5 text-xs font-bold text-white shadow-xs hover:bg-indigo-700 active:scale-98 transition"
                        >
                          <Search className="h-3.5 w-3.5" />
                          <span>Apply Filters</span>
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              )}
            </div>

            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={handleResetFilters}
                title="Reset all filters"
                className="flex h-8 w-8 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-800 transition"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="overflow-auto max-h-[calc(100vh-320px)] min-h-[350px] rounded-xl border border-slate-200 shadow-xs">
        <table className="w-full min-w-[980px] text-left text-sm border-separate border-spacing-0">
          <thead className="sticky top-0 z-10 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500 shadow-xs">
            <tr>
              <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 border-b border-slate-200">Corporate Code</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 border-b border-slate-200">Corporate Name</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 border-b border-slate-200">Contact Person</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 border-b border-slate-200">Email</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 border-b border-slate-200">Mobile</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 border-b border-slate-200">City</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 border-b border-slate-200">State</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 border-b border-slate-200">Created Date</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 border-b border-slate-200">Status</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-right border-b border-slate-200">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-slate-400">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin text-indigo-600" />
                </td>
              </tr>
            )}
            {!loading && !filteredRows.length && (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-slate-400">
                  No pending corporate onboarding records found
                </td>
              </tr>
            )}
            {!loading &&
              filteredRows.map((corp) => {
                const isBusy = busyId === (corp.id || corp.code);
                return (
                  <tr key={corp.id || corp.code} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-semibold text-indigo-600">{corp.code || "—"}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">{corp.name || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{corp.userName || corp.username || corp.userCode || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{corp.email || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{corp.mobile || "—"}</td>
                    <td className="px-4 py-3 text-slate-500">{corp.city || "—"}</td>
                    <td className="px-4 py-3 text-slate-500">{corp.stateName || corp.state || "—"}</td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{rowCreatedDate(corp)}</td>
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700">
                        Pending
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <ActionButton
                          icon={Eye}
                          label="View"
                          tone="slate"
                          onClick={() => onView(corp)}
                        />
                        <ActionButton
                          icon={Check}
                          label="Approve"
                          tone="emerald"
                          disabled={isBusy}
                          onClick={() => onApprove(corp.id || corp.code)}
                        />
                        <ActionButton
                          icon={X}
                          label="Reject"
                          tone="rose"
                          disabled={isBusy}
                          onClick={() => onReject(corp.id || corp.code)}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {!loading && totalRecords > 0 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(newSize) => {
            setPageSize(newSize);
            setPage(0);
          }}
          totalElements={totalRecords}
        />
      )}
    </div>
  );
}

export default function CCOnboardingWorkflow({ mode = "creator", defaultOnboardingType = "CC" }) {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useWorkflowUser(mode);
  const isCreator = user.persona === "CREATOR";

  const [activeType, setActiveType] = useState(defaultOnboardingType);
  const [view, setView] = useState(isCreator ? "form" : "list");
  const [formMode, setFormMode] = useState("create");
  const [approveBusy, setApproveBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [selectedCorporate, setSelectedCorporate] = useState(null);
  const [corporateActionBusyId, setCorporateActionBusyId] = useState(null);
  const [corporateRefreshKey, setCorporateRefreshKey] = useState(0);
  const { detail, workflowAction } = useSelector(selectOnboardingWorkflow);

  const [approverFilters, setApproverFilters] = useState(DEFAULT_APPROVER_FILTERS);
  const [approverPagination, setApproverPagination] = useState(DEFAULT_APPROVER_PAGINATION);

  const handleApproveCorporate = async (id) => {
    if (!id) return;
    try {
      setCorporateActionBusyId(id);
      const payload = {
        modifiedBy: currentUserCode() || "ADMIN",
        publishStatus: "A",
      };
      await corporateApi.approveCorporate(id, payload);
      toast.success("Corporate has been approved successfully!");
      if (view === "form") {
        goToList();
      }
      setCorporateRefreshKey((prev) => prev + 1);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to approve corporate. Please try again.");
    } finally {
      setCorporateActionBusyId(null);
    }
  };

  const handleRejectCorporate = async (id) => {
    if (!id) return;
    try {
      setCorporateActionBusyId(id);
      const payload = {
        modifiedBy: currentUserCode() || "ADMIN",
        publishStatus: "R",
      };
      await corporateApi.approveCorporate(id, payload);
      toast.success("Corporate has been rejected successfully!");
      if (view === "form") {
        goToList();
      }
      setCorporateRefreshKey((prev) => prev + 1);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to reject corporate. Please try again.");
    } finally {
      setCorporateActionBusyId(null);
    }
  };

  useEffect(() => {
    setActiveType(defaultOnboardingType);
    setView(isCreator ? "form" : "list");
    setFormMode("create");
    setSearchParams({});
    setApproverFilters(DEFAULT_APPROVER_FILTERS);
    setApproverPagination(DEFAULT_APPROVER_PAGINATION);
    if (isCreator) dispatch(resetWizard());
  }, [mode, defaultOnboardingType]);

  const goToList = useCallback(() => {
    setSearchParams({});
    setView("list");
  }, [setSearchParams]);

  const goToAddNew = useCallback(() => {
    dispatch(resetWizard());
    setSearchParams({});
    setFormMode("create");
    setView("form");
  }, [dispatch, setSearchParams]);

  const goToEdit = useCallback(
    ({ centerCode, draftKey }) => {
      const next = {};
      if (draftKey) next.draftKey = draftKey;
      if (centerCode) next.centerCode = centerCode;

      if (centerCode && !draftKey) {
        dispatch(beginEdit({ draftKey: null }));
      }

      setSearchParams(next);
      setFormMode("edit");
      setView("form");
    },
    [dispatch, setSearchParams]
  );

  const goToReview = useCallback(
    ({ centerCode, draftKey }) => {
      const next = {};
      if (centerCode) next.centerCode = centerCode;
      if (draftKey) next.draftKey = draftKey;
      setSearchParams(next);
      setFormMode("review");
      setView("form");
    },
    [setSearchParams]
  );

  const goToViewOnly = useCallback(
    ({ centerCode, draftKey }) => {
      const next = {};
      if (centerCode) next.centerCode = centerCode;
      if (draftKey) next.draftKey = draftKey;
      setSearchParams(next);
      setFormMode("view");
      setView("form");
    },
    [setSearchParams]
  );

  const activeCenterCode = searchParams.get("centerCode") || "";

  const handleApprove = async () => {
    if (!activeCenterCode) return;
    setApproveBusy(true);
    await dispatch(
      approveCentre({ centerCode: activeCenterCode, remarks: user.remarks || "Approved", userCode: user.code })
    );
    setApproveBusy(false);
    goToList();
  };

  const handleReject = async (remarks) => {
    if (!activeCenterCode) return;
    setApproveBusy(true);
    await dispatch(rejectCentre({ centerCode: activeCenterCode, remarks, userCode: user.code }));
    setApproveBusy(false);
    setRejectOpen(false);
    goToList();
  };

  const formKey = `${mode}-${activeType}-${formMode}-${searchParams.get("draftKey") || ""}-${searchParams.get("centerCode") || ""}`;

  const pageTitle = isCreator
    ? activeType === "LAB"
      ? "Lab Onboarding"
      : "CC Onboarding"
    : activeType === "ROUTED_LAB"
    ? "Routed Lab Orders"
    : activeType === "CORPORATE"
    ? "Corporate Approval"
    : user.label;

  return (
    <div className="min-h-screen w-full bg-slate-50 px-4 pt-3 pb-6">
      <div className="w-full max-w-[1600px] mx-auto">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-600 text-white shadow-2xs">
              <FlaskConical className="h-5 w-5" />
            </div>
            <div>
              <span className="font-bold text-slate-900">CureBay</span>
              <span className="ml-1.5 text-sm text-slate-400">{pageTitle}</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {isCreator && view === "form" && (
              <button
                type="button"
                onClick={goToList}
                className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition shadow-2xs"
              >
                <ListIcon className="h-4 w-4" /> View List
              </button>
            )}
            {!isCreator && view === "form" && (
              <button
                type="button"
                onClick={goToList}
                className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition shadow-2xs"
              >
                <ChevronLeft className="h-4 w-4" /> Back to List
              </button>
            )}
            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-2xs">
              <UserCircle2 className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-700">{user.name || user.code || "—"}</span>
            </div>
          </div>
        </div>

        {view === "list" ? (
          <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-100 sm:p-5">
            {!isCreator && (
              <div className="mb-5 flex border-b border-slate-200 pb-3">
                <div className="inline-flex rounded-xl bg-slate-100 p-1">
                  <button
                    type="button"
                    onClick={() => {
                      setActiveType("CC");
                      setApproverPagination((p) => ({ ...p, page: 0 }));
                    }}
                    className={`flex items-center gap-2 rounded-lg px-4 py-2 text-xs font-bold transition ${
                      activeType === "CC"
                        ? "bg-white text-indigo-700 shadow-sm"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    <Building2 className="h-4 w-4" /> Collection Centers (CC)
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveType("LAB");
                      setApproverPagination((p) => ({ ...p, page: 0 }));
                    }}
                    className={`flex items-center gap-2 rounded-lg px-4 py-2 text-xs font-bold transition ${
                      activeType === "LAB"
                        ? "bg-white text-indigo-700 shadow-sm"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    <TestTube2 className="h-4 w-4" /> Diagnostic Labs (LAB)
                  </button>
                  {(mode === "financeApproval" || user.approverLevel === 2) && (
                    <button
                      type="button"
                      onClick={() => {
                        setActiveType("ROUTED_LAB");
                      }}
                      className={`flex items-center gap-2 rounded-lg px-4 py-2 text-xs font-bold transition ${
                        activeType === "ROUTED_LAB"
                          ? "bg-white text-indigo-700 shadow-sm"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      <ArrowRightLeft className="h-4 w-4" /> Routed Lab Orders
                    </button>
                  )}
                  {(mode === "financeApproval" || user.approverLevel === 2) && (
                    <button
                      type="button"
                      onClick={() => {
                        setActiveType("CORPORATE");
                      }}
                      className={`flex items-center gap-2 rounded-lg px-4 py-2 text-xs font-bold transition ${
                        activeType === "CORPORATE"
                          ? "bg-white text-indigo-700 shadow-sm"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      <Briefcase className="h-4 w-4" /> Corporate
                    </button>
                  )}
                </div>
              </div>
            )}

            {activeType === "ROUTED_LAB" ? (
              <RoutedLabOrdersList user={user} />
            ) : activeType === "CORPORATE" ? (
              <CorporatePendingList
                refreshKey={corporateRefreshKey}
                busyId={corporateActionBusyId}
                onView={(corp) => {
                  setSelectedCorporate(corp);
                  setFormMode("view");
                  setView("form");
                }}
                onApprove={handleApproveCorporate}
                onReject={handleRejectCorporate}
              />
            ) : isCreator ? (
              <CreatorList
                onEdit={goToEdit}
                onView={goToViewOnly}
                onAddNew={goToAddNew}
                onboardingType={activeType}
              />
            ) : (
              <ApproverList
                user={user}
                onView={goToReview}
                onEdit={goToEdit}
                filters={approverFilters}
                onFiltersChange={setApproverFilters}
                pagination={approverPagination}
                onPaginationChange={setApproverPagination}
                onboardingType={activeType}
              />
            )}
          </div>
        ) : (
          <>
            {activeType === "CORPORATE" ? (
              <CorporateList
                key={formKey}
                readOnly={true}
                initialCorporate={selectedCorporate}
                onExit={goToList}
                onApprove={handleApproveCorporate}
                onReject={handleRejectCorporate}
                approving={corporateActionBusyId === (selectedCorporate?.id || selectedCorporate?.code)}
                rejecting={corporateActionBusyId === (selectedCorporate?.id || selectedCorporate?.code)}
              />
            ) : activeType === "LAB" ? (
              <LabOnboarding
                key={formKey}
                readOnly={formMode === "review" || formMode === "view"}
                centerCode={activeCenterCode}
                draftKey={searchParams.get("draftKey")}
                onExit={goToList}
                onApprove={formMode === "review" ? handleApprove : null}
                onReject={formMode === "review" ? () => setRejectOpen(true) : null}
                approving={approveBusy}
                rejecting={approveBusy}
              />
            ) : (
              <CCOnboarding
                key={formKey}
                readOnly={formMode === "review" || formMode === "view"}
                onExit={goToList}
                onApprove={formMode === "review" ? handleApprove : null}
                onReject={formMode === "review" ? () => setRejectOpen(true) : null}
                approving={approveBusy}
                rejecting={approveBusy}
              />
            )}

            <RemarksModal
              open={rejectOpen}
              title={activeType === "LAB" ? "Reject Diagnostic Lab" : "Reject Collection Center"}
              ccName={
                detail?.data?.tabs?.[0]?.data?.labName ||
                detail?.data?.tabs?.[0]?.data?.ccName ||
                detail?.data?.tabs?.[0]?.data?.name ||
                detail?.data?.name ||
                ""
              }
              ccCode={activeCenterCode}
              onCancel={() => setRejectOpen(false)}
              onConfirm={handleReject}
            />
            {workflowAction.status === "failed" && (
              <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-600">{workflowAction.error}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}